import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion";
import useSEO from "@/hooks/useSEO";
import { SITE } from "@/config/site";

const Section = ({ title, children }) => (
  <div className="mt-14">
    <h2 className="font-display text-2xl sm:text-3xl tracking-tight">{title}</h2>
    <div className="mt-5 space-y-4 text-sm sm:text-base leading-relaxed text-ink/65">
      {children}
    </div>
  </div>
);

const PrivacyContent = () => (
  <>
    <Section title="1. Titolare del trattamento">
      <p>
        Titolare del trattamento è Michele Ceccarelli, ditta individuale — P.IVA 04202351203 —
        titolare del brand Nutrimove Lab, con studi a Bologna (Via Scipione Dal Ferro 4, Palazzo
        Dehon Media) e a Casalecchio di Reno (Via XX Settembre 9 e Via Porrettana 393). Per
        qualsiasi richiesta relativa alla privacy puoi scrivere a{" "}
        <a href={SITE.contacts.emailHref} className="underline underline-offset-2 hover:text-leaf-deep">
          {SITE.contacts.email}
        </a>{" "}
        o chiamare il{" "}
        <a href={SITE.contacts.phoneHref} className="underline underline-offset-2 hover:text-leaf-deep">
          {SITE.contacts.phone}
        </a>
        .
      </p>
    </Section>

    <Section title="2. Quali dati raccoglie questo sito">
      <p>
        Questo sito non prevede registrazione, aree riservate o moduli di contatto: non raccoglie
        automaticamente dati personali dei visitatori.
      </p>
      <p>
        Se mi contatti volontariamente — via email, telefono o WhatsApp — vengono trattati i dati
        che scegli di condividere (nome, recapiti e contenuto del messaggio) esclusivamente per
        risponderti e organizzare l'eventuale appuntamento.
      </p>
      <p>
        Ti chiedo di non inviare dati sanitari o documentazione medica tramite WhatsApp o email:
        le informazioni relative alla salute vengono raccolte e trattate solo nell'ambito della
        prestazione professionale, nel rispetto della normativa sui dati particolari (art. 9 GDPR).
      </p>
    </Section>

    <Section title="3. Finalità e base giuridica">
      <p>
        I dati forniti volontariamente sono trattati per rispondere alle richieste di contatto e
        per la gestione precontrattuale degli appuntamenti (art. 6, par. 1, lett. b GDPR). L'invio
        dei dati è facoltativo; senza di essi non è possibile rispondere alla richiesta.
      </p>
    </Section>

    <Section title="4. Prenotazioni tramite MioDottore">
      <p>
        Le prenotazioni online avvengono tramite MioDottore (Docplanner), piattaforma esterna
        raggiungibile dai pulsanti di prenotazione di questo sito. I dati inseriti su MioDottore
        sono trattati secondo l'informativa privacy di quella piattaforma, disponibile sul loro
        sito.
      </p>
    </Section>

    <Section title="5. Servizi di terze parti presenti sul sito">
      <ul className="list-disc space-y-2 pl-5">
        <li>
          <strong>WhatsApp e Instagram (Meta Platforms)</strong> — link esterni verso i rispettivi
          servizi, con proprie informative privacy.
        </li>
        <li>
          <strong>Google Maps (Google Ireland)</strong> — mappa interattiva delle sedi, se attiva;
          in alternativa la mappa utilizza OpenStreetMap (OpenStreetMap Foundation).
        </li>
        <li>
          <strong>Google Analytics 4 (Google Ireland)</strong> — statistiche di utilizzo del sito,
          installate solo dopo il tuo consenso tramite il banner cookie.
        </li>
      </ul>
    </Section>

    <Section title="6. Conservazione dei dati">
      <p>
        I messaggi di contatto sono conservati per il tempo necessario a gestire la richiesta. Se
        nasce un rapporto professionale, i dati sono conservati secondo i termini di legge
        applicabili alla documentazione sanitaria e fiscale.
      </p>
    </Section>

    <Section title="7. I tuoi diritti">
      <p>
        Ai sensi del GDPR puoi chiedere in qualsiasi momento accesso, rettifica, cancellazione,
        limitazione del trattamento, opposizione e portabilità dei tuoi dati scrivendo a{" "}
        <a href={SITE.contacts.emailHref} className="underline underline-offset-2 hover:text-leaf-deep">
          {SITE.contacts.email}
        </a>
        . Hai inoltre diritto di reclamo al Garante per la protezione dei dati personali
        (www.garanteprivacy.it).
      </p>
    </Section>

    <Section title="8. Modifiche a questa informativa">
      <p>
        Questa informativa può essere aggiornata nel tempo per riflettere modifiche del sito o
        della normativa. La versione pubblicata su questa pagina è quella in vigore.
      </p>
    </Section>
  </>
);

const CookieContent = () => (
  <>
    <Section title="1. Cosa sono i cookie">
      <p>
        I cookie sono piccoli file di testo che i siti salvano sul dispositivo. Questo sito utilizza
        anche il localStorage del browser, una tecnologia equivalente, per ricordare le tue
        preferenze.
      </p>
    </Section>

    <Section title="2. Cookie tecnici (sempre attivi)">
      <p>
        Il sito salva esclusivamente la tua scelta sul consenso (localStorage
        "nutrimove-consent"), necessaria al funzionamento del banner. I cookie tecnici non
        richiedono consenso ai sensi della normativa vigente.
      </p>
    </Section>

    <Section title="3. Cookie di analisi (solo con il tuo consenso)">
      <p>
        Solo se premi "Accetta" nel banner, il sito attiva Google Analytics 4 per raccogliere
        statistiche aggregate e anonime sull'utilizzo delle pagine (visite e clic sui pulsanti di
        prenotazione e contatto). Se rifiuti, nessun cookie di analisi viene installato.
      </p>
    </Section>

    <Section title="4. Contenuti e servizi esterni">
      <p>
        La mappa delle sedi (Google Maps oppure OpenStreetMap) e i link verso MioDottore, WhatsApp
        e Instagram possono comportare il trasferimento di dati di navigazione verso i rispettivi
        fornitori quando li utilizzi, secondo le loro informative.
      </p>
    </Section>

    <Section title="5. Come gestire o revocare il consenso">
      <p>
        Puoi revocare il consenso in qualsiasi momento cancellando i dati di questo sito dalle
        impostazioni del browser (cronologia → cookie e dati dei siti): alla visita successiva il
        banner ti verrà riproposto.
      </p>
    </Section>

    <Section title="6. Aggiornamenti">
      <p>
        Questa Cookie Policy può essere aggiornata in caso di modifiche ai servizi utilizzati dal
        sito. La versione pubblicata qui è quella in vigore.
      </p>
    </Section>
  </>
);

export default function Legal({ title, kind }) {
  useSEO({
    title: `${title} | Nutrimove Lab`,
    description: `${title} del sito di Nutrimove Lab — Dott. Michele Ceccarelli, Biologo Nutrizionista e Dott. in Scienze Motorie.`,
  });

  return (
    <section className="mx-auto max-w-3xl px-5 sm:px-8 pt-32 sm:pt-44 pb-24 sm:pb-32">
      <Reveal>
        <p className="eyebrow text-leaf-deep">Nutrimove Lab</p>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight">{title}</h1>
        <p className="mt-6 text-sm leading-relaxed text-ink/50">
          Ultimo aggiornamento: luglio 2026
        </p>
        {kind === "privacy" ? <PrivacyContent /> : <CookieContent />}
        <div className="mt-16 rounded-2xl border border-ink/10 bg-mist p-6">
          <p className="text-xs leading-relaxed text-ink/50">
            Questo documento riflette i servizi effettivamente utilizzati dal sito. Titolare del
            trattamento: Michele Ceccarelli, ditta individuale, P.IVA 04202351203 — titolare del
            brand Nutrimove Lab. Per qualsiasi chiarimento:{" "}
            <a href={SITE.contacts.emailHref} className="underline underline-offset-2 hover:text-leaf-deep">
              {SITE.contacts.email}
            </a>
            .
          </p>
        </div>
        <Link
          to="/"
          data-testid="legal-back-home"
          className="mt-10 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-200 hover:text-leaf-deep"
        >
          Torna alla Home
        </Link>
      </Reveal>
    </section>
  );
}
